const TIMEOUT_MS = 30_000;

export type RemittanceSource = {
  countryCode: string;
  displayName: string;
  corridorCode: string;
  sourceCurrency: string;
};

export type RemittanceRailMinimum = {
  corridorCode: string;
  currencyCode: string;
  minTypedAmount: number;
};

export type RemittanceRailBounds = {
  corridorCode: string;
  typedCurrency: string | null;
  minTypedAmount: number | null;
  maxTypedAmount: number | null;
  minReceiveUsd: number | null;
  maxReceiveUsd: number | null;
  sellRate: number | null;
};

export type RemittanceDestination = {
  countryCode: string;
  displayName: string;
};

export type RemittanceChannel = {
  channelTypeId: number;
  code: string;
  displayName: string;
  allowedReceiveDeliveryModes: string[];
};

export class RemittanceApiError extends Error {
  readonly errorCode?: string;

  constructor(message: string, errorCode?: string) {
    super(message);
    this.name = 'RemittanceApiError';
    this.errorCode = errorCode;
  }
}

export type RemittanceEstimate = {
  targetAmountUsd?: number | null;
  requiredSendAmount?: number | null;
  lulpayFeeAmount?: number | null;
  lulpayFeePercent?: number | null;
  sourceCurrencyCode?: string | null;
  netLocalAmount?: number | null;
  grossLocalAmount?: number | null;
  localCurrencyCode?: string | null;
  receiveDeliveryMode?: string | null;
  bridgeBuyRateToUsd?: number | null;
  minTypedAmount?: number | null;
  maxTypedAmount?: number | null;
  minReceiveUsd?: number | null;
  maxReceiveUsd?: number | null;
};

function apiBase(): string {
  const raw = process.env.REACT_APP_API_BASE_URL ?? '';
  return raw.replace(/\/+$/, '');
}

async function getJson<T>(path: string, signal?: AbortSignal): Promise<T> {
  const controller = new AbortController();
  const timer = window.setTimeout(() => controller.abort(), TIMEOUT_MS);
  const abortFromCaller = () => controller.abort();
  signal?.addEventListener('abort', abortFromCaller);
  try {
    const response = await fetch(`${apiBase()}${path}`, {
      method: 'GET',
      headers: { Accept: 'application/json' },
      signal: controller.signal,
    });
    const text = await response.text();
    let body: unknown = null;
    if (text) {
      try {
        body = JSON.parse(text);
      } catch {
        body = null;
      }
    }
    if (!response.ok) {
      const record =
        body && typeof body === 'object'
          ? (body as { message?: string; errorCode?: string })
          : null;
      throw new RemittanceApiError(record?.message || 'Request failed', record?.errorCode);
    }
    if (body && typeof body === 'object' && 'data' in body) {
      return (body as { data: T }).data;
    }
    return body as T;
  } finally {
    window.clearTimeout(timer);
    signal?.removeEventListener('abort', abortFromCaller);
  }
}

export function fetchRemittanceSources(signal?: AbortSignal): Promise<RemittanceSource[]> {
  return getJson('/api/v1/lookups/reference/remittance-sources', signal);
}

export function fetchRemittanceRailMinimums(signal?: AbortSignal): Promise<RemittanceRailMinimum[]> {
  return getJson('/api/v1/lookups/reference/remittance-rail-minimums', signal);
}

export function fetchRemittanceRailBounds(
  corridorCode: string,
  signal?: AbortSignal,
): Promise<RemittanceRailBounds> {
  const code = encodeURIComponent(corridorCode);
  return getJson(`/api/v1/lookups/reference/remittance-rail-bounds?corridorCode=${code}`, signal);
}

export function fetchRemittanceDestinations(signal?: AbortSignal): Promise<RemittanceDestination[]> {
  return getJson('/api/v1/lookups/reference/remittance-destinations', signal);
}

export function fetchRemittanceChannels(
  countryCode: string,
  signal?: AbortSignal,
): Promise<RemittanceChannel[]> {
  const code = encodeURIComponent(countryCode);
  return getJson(`/api/v1/lookups/reference/remittance-destinations/${code}/channels`, signal);
}

export function fetchRemittanceEstimate(
  query: {
    sourceCountryCode: string;
    destinationCountryCode: string;
    receiveDeliveryMode: string;
    disbursementChannelId: number;
    targetAmountUsd?: number;
    sendAmount?: number;
  },
  signal?: AbortSignal,
): Promise<RemittanceEstimate> {
  const params = new URLSearchParams();
  params.set('sourceCountryCode', query.sourceCountryCode);
  params.set('destinationCountryCode', query.destinationCountryCode);
  params.set('receiveDeliveryMode', query.receiveDeliveryMode);
  params.set('disbursementChannelId', String(query.disbursementChannelId));
  if (query.targetAmountUsd != null) {
    params.set('targetAmountUsd', String(query.targetAmountUsd));
  }
  if (query.sendAmount != null) {
    params.set('sendAmount', String(query.sendAmount));
  }
  return getJson(`/api/v1/lookups/reference/remittance-estimate?${params.toString()}`, signal);
}
